import './index.css';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { createContext, forwardRef, useContext, useMemo, useEffect, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { Action } from '@hh.ru/magritte-ui-action';
import { Breakpoint, useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { DotPages } from '@hh.ru/magritte-ui-dot-pages';
import { ChevronLeftOutlinedSize24, ChevronRightOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { u as useScrollContainer, s as styles } from './useScrollContainer-B8GCFuOp.js';
import { VSpacing } from '@hh.ru/magritte-ui-spacing';
import '@hh.ru/magritte-internal-custom-scroll';
import './types.js';
import './utils.js';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noopFunc = () => { };
const ScrollContext = createContext({
    registerItem: noopFunc,
    removeItem: noopFunc,
    scrollToItem: noopFunc,
});
const useScrollContext = () => useContext(ScrollContext);
const BREAKPOINTS_LIST = [Breakpoint.XS, Breakpoint.S, Breakpoint.M, Breakpoint.L, Breakpoint.XL, Breakpoint.XXL];
const NO_BUTTONS_START_FROM_BREAKPOINT = Breakpoint.S;
const NO_TRANSLATE = { transform: 'translate3d(0px, 0, 0)' };
/**
 * По переданным параметрам коррекции возвращает значения отступов для текущего брейкпоинта
 * @param props Параметры коррекции
 * @param breakpoint Текуший брейкпоинт
 * @returns Объект содержащий значения отступов
 */
const getActualCorrection = (props, breakpoint) => {
    const breakpointIndex = BREAKPOINTS_LIST.indexOf(breakpoint);
    let leftCorrection;
    let rightCorrection;
    // Проходимся по всем брейкпоинтам, начиная с текущего в сторону меньших разрешений
    for (let i = breakpointIndex; i >= 0; i--) {
        const breakpointName = BREAKPOINTS_LIST[i].toUpperCase();
        // Ключи, которые могут быть в параметрах коррекции (SpacingCorrectionProps)
        const leftCorrectionKey = `correctionLeft${breakpointName}`;
        const rightCorrectionKey = `correctionRight${breakpointName}`;
        const commonCorrectionKey = `correction${breakpointName}`;
        // Если для текущего брейкпоинта есть параметры коррекции для левой/правой стороны, то берем их
        props[leftCorrectionKey] !== undefined &&
            leftCorrection === undefined &&
            (leftCorrection = props[leftCorrectionKey]);
        props[rightCorrectionKey] !== undefined &&
            rightCorrection === undefined &&
            (rightCorrection = props[rightCorrectionKey]);
        // Если для левой или правой стороны не найден параметр коррекции, то пробуем взять параметр correction для
        // брейкпоинта который сейчас обрабатываем в цикле, например correctionM
        if (props[commonCorrectionKey] !== undefined) {
            leftCorrection === undefined && (leftCorrection = props[commonCorrectionKey]);
            rightCorrection === undefined && (rightCorrection = props[commonCorrectionKey]);
        }
        // Если нашли значения для обеих сторон - возращаем
        if (leftCorrection !== undefined && rightCorrection !== undefined) {
            return { leftCorrection, rightCorrection };
        }
    }
    // Если не нашли значение для какой то из сторон, то берем значение из correction, без уточнения брейкпоинта
    return {
        leftCorrection: leftCorrection === undefined ? props.correction || 0 : leftCorrection,
        rightCorrection: rightCorrection === undefined ? props.correction || 0 : rightCorrection,
    };
};
const ScrollableContainerComponent = ({ children, disableButtons: _disableButtons, correction = 0, correctionXS, correctionS, correctionM, correctionL, correctionXL, correctionXXL, correctionLeftXS, correctionLeftS, correctionLeftM, correctionLeftL, correctionLeftXL, correctionLeftXXL, correctionRightXS, correctionRightS, correctionRightM, correctionRightL, correctionRightXL, correctionRightXXL, showDotPages, dotPagesProps, onChangeDot, onScroll, ...rest }, controlsRef) => {
    const breakpoint = useBreakpoint();
    const breakpointIndex = BREAKPOINTS_LIST.indexOf(breakpoint.breakpoint);
    const noButtonBreakpointIndex = BREAKPOINTS_LIST.indexOf(NO_BUTTONS_START_FROM_BREAKPOINT);
    // use corrections and cast container to mobile view
    const disableButtons = _disableButtons || breakpointIndex <= noButtonBreakpointIndex;
    const spacingCorrectionStyles = useMemo(() => {
        // component use default button
        if (!disableButtons) {
            return { paddingLeft: 0, paddingRight: 0 };
        }
        const correctionProps = {
            correction,
            correctionXS,
            correctionS,
            correctionM,
            correctionL,
            correctionXL,
            correctionXXL,
            correctionLeftXS,
            correctionLeftS,
            correctionLeftM,
            correctionLeftL,
            correctionLeftXL,
            correctionLeftXXL,
            correctionRightXS,
            correctionRightS,
            correctionRightM,
            correctionRightL,
            correctionRightXL,
            correctionRightXXL,
        };
        const correctionValue = getActualCorrection(correctionProps, breakpoint.breakpoint);
        return {
            marginLeft: -correctionValue.leftCorrection,
            marginRight: -correctionValue.rightCorrection,
            paddingLeft: correctionValue.leftCorrection,
            paddingRight: correctionValue.rightCorrection,
        };
    }, [
        disableButtons,
        correction,
        correctionXS,
        correctionS,
        correctionM,
        correctionL,
        correctionXL,
        correctionXXL,
        correctionLeftXS,
        correctionLeftS,
        correctionLeftM,
        correctionLeftL,
        correctionLeftXL,
        correctionLeftXXL,
        correctionRightXS,
        correctionRightS,
        correctionRightM,
        correctionRightL,
        correctionRightXL,
        correctionRightXXL,
        breakpoint.breakpoint,
    ]);
    const { containerRef, positionContainerRef, wrapperRef, registerItem, removeItem, scrollToItem, scrollToFirstItem, scrollToLastItem, scrollSide, initScrollHandlers, countItems, isScrollRightDisabled, isScrollLeftDisabled, activeDot, } = useScrollContainer(spacingCorrectionStyles.paddingLeft, spacingCorrectionStyles.paddingRight, disableButtons, showDotPages, onScroll);
    useEffect(initScrollHandlers, [initScrollHandlers]);
    useImperativeHandle(controlsRef, () => ({
        scrollLeft: () => scrollSide(),
        scrollRight: () => scrollSide(true),
        scrollToFirstItem: () => scrollToFirstItem(),
        scrollToLastItem: () => scrollToLastItem(),
        isScrollRightDisabled: () => isScrollRightDisabled(),
        isScrollLeftDisabled: () => isScrollLeftDisabled(),
    }), [scrollSide, scrollToFirstItem, scrollToLastItem, isScrollRightDisabled, isScrollLeftDisabled]);
    useEffect(() => {
        onChangeDot?.(activeDot);
    }, [onChangeDot, activeDot]);
    return (jsxs("div", { children: [jsxs("div", { ref: wrapperRef, "data-qa": "scrollable-container__wrapper", className: classnames(styles.scrollWrapper, {
                    [styles.noButtons]: disableButtons,
                }), ...rest, children: [jsx("div", { className: styles.scrollableContainer, style: disableButtons ? spacingCorrectionStyles : {}, ref: containerRef, "data-qa": "scrollable-container__overflow-container", children: jsx("div", { "data-qa": "scrollable-container__position-container", className: styles.positionContainer, style: NO_TRANSLATE, ref: positionContainerRef, children: jsx(ScrollContext.Provider, { value: { registerItem, removeItem, scrollToItem }, children: children }) }) }), !disableButtons && (jsxs(Fragment, { children: [jsx("div", { className: classnames(styles.scrollButton, styles.scrollButtonLeft), children: jsx(Action, { tabIndex: -1, mode: "secondary", style: "neutral", icon: ChevronLeftOutlinedSize24, showBackground: true, onClick: () => scrollSide(), "data-qa": "scrollable-container__scroll-button-left" }) }), jsx("div", { className: classnames(styles.scrollButton, styles.scrollButtonRight), children: jsx(Action, { tabIndex: -1, mode: "secondary", style: "neutral", icon: ChevronRightOutlinedSize24, showBackground: true, onClick: () => scrollSide(true), "data-qa": "scrollable-container__scroll-button-right" }) })] }))] }), showDotPages && countItems > 1 && (jsxs("div", { children: [jsx(VSpacing, { default: 8 }), jsx("div", { className: styles.dotContainer, children: jsx(DotPages, { active: activeDot, count: countItems, "data-qa": "scrollable-container__dot-pages", ...dotPagesProps }) })] }))] }));
};
const ScrollableContainer = forwardRef(ScrollableContainerComponent);
ScrollableContainer.displayName = 'ScrollableContainer';

export { ScrollableContainer, ScrollableContainerComponent, useScrollContext };
//# sourceMappingURL=ScrollableContainer.js.map
